import { AdminPageHeader } from "@admin-ui/components/composed/AdminPageHeader";
import { AdminLayout } from "@admin-ui/components/frames/AdminLayout";
import { Badge } from "@admin-ui/components/primitives/base/badges/badges";
import { Button } from "@admin-ui/components/primitives/base/buttons/button";
import { Dropdown } from "@admin-ui/components/primitives/base/dropdown/dropdown";
import { formatCount } from "@admin-ui/utils/format-count";
import { Head, router } from "@inertiajs/react";
import {
  adminProgramPath,
  adminProgramPublicationPath,
  adminProgramsPath,
  adminProgramsReindexPath,
  newAdminProgramPath,
} from "@travily/routes";
import type AdminProgramsIndex from "@travily/types/pages/AdminProgramsIndex";
import { Plus, RefreshCcw01 } from "@untitledui/icons";
import { type ReactNode, useCallback } from "react";
import { useAdminLayoutAccount } from "@/hooks/admin/use-admin-layout-account";
import { useAdminListNavigation } from "@/hooks/admin/use-admin-list-navigation";
import AdminPersistentLayout from "@/layouts/admin-persistent-layout";
import { IndexView, type ProgramFilters } from "./index.view";

type AdminProgramsIndexPageProps = AdminProgramsIndex;

const FILTER_TO_RANSACK: Record<keyof ProgramFilters, string> = {
  search: "name_or_account_handle_cont",
};

function AdminProgramsIndexPage({ programs, pagy, sort, sort_dir, filters }: AdminProgramsIndexPageProps) {
  const account = useAdminLayoutAccount();

  const { handleSortChange, handlePageChange, handleSearchChange } = useAdminListNavigation<ProgramFilters>({
    basePath: adminProgramsPath(),
    filters,
    sort,
    sortDir: sort_dir,
    filterToRansack: FILTER_TO_RANSACK,
  });

  const handleNewProgram = useCallback(() => router.get(newAdminProgramPath()), []);

  const handleEditProgram = useCallback((programId: number) => router.get(adminProgramPath(programId)), []);

  const handleDeleteProgram = useCallback((programId: number) => router.delete(adminProgramPath(programId)), []);

  const handlePublishProgram = useCallback(
    (programId: number) => router.post(adminProgramPublicationPath(programId), {}, { preserveScroll: true }),
    [],
  );

  const handleUnpublishProgram = useCallback(
    (programId: number) => router.delete(adminProgramPublicationPath(programId), { preserveScroll: true }),
    [],
  );

  const handleReindex = useCallback(() => router.post(adminProgramsReindexPath(), {}, { preserveScroll: true }), []);

  const programCount = formatCount(pagy.count, "program");

  return (
    <AdminLayout activeUrl={adminProgramsPath()} account={account}>
      <Head title="Programs - Admin" />

      <AdminPageHeader
        breadcrumbs={[]}
        title="Programs"
        titleBadge={
          <Badge color="gray" size="sm">
            {programCount}
          </Badge>
        }
        subtitle="Learning programs hosted by community accounts"
        actions={
          <>
            <Button size="sm" color="primary" iconLeading={Plus} onClick={handleNewProgram}>
              New program
            </Button>
            <Dropdown.Root>
              <Dropdown.DotsButton />
              <Dropdown.Popover className="w-min">
                <Dropdown.Menu
                  onAction={(key) => {
                    if (key === "reindex") handleReindex();
                  }}
                >
                  <Dropdown.Item id="reindex" icon={RefreshCcw01} label="Reindex programs" />
                </Dropdown.Menu>
              </Dropdown.Popover>
            </Dropdown.Root>
          </>
        }
      />

      <div className="mx-auto flex w-full max-w-container flex-col gap-6 px-4 lg:gap-8 lg:px-8">
        <IndexView
          programs={programs}
          pagy={pagy}
          sort={sort}
          sortDir={sort_dir}
          filters={filters}
          onSortChange={handleSortChange}
          onPageChange={handlePageChange}
          onSearchChange={handleSearchChange}
          onEditProgram={handleEditProgram}
          onDeleteProgram={handleDeleteProgram}
          onPublishProgram={handlePublishProgram}
          onUnpublishProgram={handleUnpublishProgram}
        />
      </div>
    </AdminLayout>
  );
}

AdminProgramsIndexPage.layout = (page: ReactNode) => <AdminPersistentLayout>{page}</AdminPersistentLayout>;

export default AdminProgramsIndexPage;
